import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Optional,
    Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { FormHelper } from '@klickdata/core/form';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceMediaTypes } from '@klickdata/core/question/src/question-type/question-type';
import { ResourceItemData, ResourceItemService, ResourceItemTypes } from '@klickdata/core/resource-item';
import { ResourceItem } from '@klickdata/core/resource-item/src/resource-item';
import { Resource } from '@klickdata/core/resource/src/resource.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MaterialItemComposerDirective } from '../material-item-composer-directive';
import { MaterialManagerCoreService } from '../material-manager-core.service';
import { MaterialsManagerCoreComponent } from '../materials-manager-core.component';
import { MaterialParentDisplayModeBottomSheetComponent } from './material-parent-display-mode-bottom-sheet/material-parent-display-mode-bottom-sheet.component';

@Component({
    selector: 'app-materials-manager-parent',
    templateUrl: './materials-manager-parent.component.html',
    styleUrls: ['./materials-manager-parent.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialsManagerParentComponent extends MaterialItemComposerDirective implements OnInit {
    @Input() readonly index: number;
    @Input() resource: Resource;
    @Input() disabled: boolean;
    @Input() active: boolean;
    public resourceItems: ResourceItem[];
    @Input() childrenIds: number[];
    @Output() onSplitText = new EventEmitter<{ blocksCount?: number; index?: number }>();
    @Output() onLoading = new EventEmitter<boolean>();
    @Output() onDownload = new EventEmitter<number[]>();
    public childrenItems$: Observable<ResourceItem[]>;
    public disableSorting: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public sortingPrio: FormControl = new FormControl(null);
    public editMode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    loading: any;
    openedIndex: number = -1;
    childrenLoaded: boolean = false;

    constructor(
        @Optional() protected host: MaterialsManagerCoreComponent,
        protected cdRef: ChangeDetectorRef,
        protected parentFormDirective: FormGroupDirective,
        protected bottomSheet: MatBottomSheet,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        protected messageService: MessageService,
        protected itemService: ResourceItemService,
        protected materialManagerCoreService: MaterialManagerCoreService,
        protected mobileService: MobileService
    ) {
        super(cdRef, parentFormDirective, materialManagerCoreService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.loading$.next(true);
        this.itemForm.addControl('children_items', this.fb.array([]));
        this.childrenItems$ = this.initContainerChildren();
        this.items.valueChanges.pipe(takeUntil(this.destroy)).subscribe((value) => {
            this.disabled = !value.length;
            this.cdr.markForCheck();
        });
    }

    initContainerChildren(): Observable<ResourceItem[]> {
        return this.itemService.getResourceItemsByParentItemId(this.itemForm.value.id).pipe(
            tap((items) => {
                this.initResourceItems(items);
                this.buildSockets();
                this.loading$.next(false);
            })
        );
    }
    get parentConnectedList() {
        return this.host?.items ?? [];
    }

    get childrenItemsCount(): number {
        return !this.childrenLoaded
            ? this.childrenIds?.length
            : this.items?.controls?.filter((item) => !item.value.deleted)?.length ?? 0;
    }

    private buildSockets() {
        this.itemService
            .listenToMaterialContainerItemsUpdate(this.itemForm.value.id)
            .pipe(
                takeUntil(this.destroy),
                filter((items) => !!items.length)
            )
            .subscribe((items) => {
                items.forEach((sourceItem) => this.replaceItemFormGroupById(sourceItem));
                this.items.markAsDirty();
                this.cdr.markForCheck();
            });
    }

    initResourceItems(items: ResourceItem[]) {
        this.resourceItems = items;
        this.items.clear();
        items.forEach((item) => {
            const itemFormGroup = this.createItemFormGroup(item);
            if (
                item.prompt_status === 'PENDING' ||
                item.prompt_status === 'PROCESSING' ||
                item.prompt_status === 'PARSING'
            ) {
                itemFormGroup.disable();
            }
            FormHelper.resetForm(itemFormGroup);
            this.items.push(itemFormGroup);
        });

        this.cdr.markForCheck();
    }
    get items() {
        return this.itemForm.get('children_items') as FormArray;
    }
    get areItemsProcessing(): boolean {
        return this.items?.controls?.some((ctrl) => ctrl.disabled);
    }

    get isDragging(): boolean {
        return this.host?.isDragging$?.value ?? false;
    }

    get isDragging$(): Observable<boolean> {
        return this.host?.isDragging$ ?? of(false);
    }
    translate(data: { language_id: number; index: number }) {
        this.onLoading.emit(true);

        const controlToTranslate = this.items.controls[data.index];
        if (!controlToTranslate) {
            this.onLoading.emit(false);
            return;
        }

        const itemToTranslate: { index: number; item: ResourceItem } = {
            index: data.index + 1,
            item: new ResourceItem({
                ...controlToTranslate.value,
                language_id: data.language_id,
                parent_id: controlToTranslate.value.id,
            }),
        };

        this.duplicateChildItem(itemToTranslate.item, itemToTranslate.index);

        this.updateItemsPromptStatus([itemToTranslate.item.id]);
        this.itemService
            .translateItems([itemToTranslate.item.id], data.language_id, null)
            .pipe(takeUntil(this.destroy))
            .subscribe(() => {
                this.onLoading.emit(false);
            });
    }
    regenerate(item: ResourceItemData, index: number) {
        if (this.isEditInBottomSheet) {
            this.editInBottomSheet();
            return;
        }
        const resourceItem = this.resourceItems.find((resourceItem) => resourceItem.id === item.id);
        if (!resourceItem) {
            return;
        }
        resourceItem.name = item.name;
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.PROMPT_REGENERATION,
            data: {
                type: 'regenerate',
                resourceItem: resourceItem,
            },
        });
        /**
         * data > {
         *  llm: string,
         *  prompt: string, >> indicates of name
         *  type: 'regenerate',
         * }
         */
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'regenerate'),
                tap(() => this.onLoading.emit(true)),
                switchMap((data) =>
                    this.itemService.regenerateItems([item.id], {
                        body: {
                            type: 'regenerate',
                            model_name: data.llm,
                            ...(!!data.prompt && { prompt: data.prompt }),
                        },
                    })
                ),
                map((items) => items[0]),
                takeUntil(this.destroy)
            )
            .subscribe((item: ResourceItem) => {
                this.items.removeAt(index);
                const newItemCtl = this.createItemFormGroup(item);
                if (
                    item.prompt_status === 'PENDING' ||
                    item.prompt_status === 'PROCESSING' ||
                    item.prompt_status === 'PARSING'
                ) {
                    newItemCtl.disable();
                }
                FormHelper.resetForm(newItemCtl);
                this.items.insert(index, newItemCtl);
                this.onLoading.emit(false);
            });
    }

    addImg(index?: number) {
        if (this.isEditInBottomSheet) {
            this.editInBottomSheet();
            return;
        }
        if (!this.resource?.id) this.toggleAutoSave(false);
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_MEDIA,
            data: {
                icon: 'add_photo_alternate',
                title: $localize`Add an image`,
                description: $localize`Please select your preferred way to add an image.`,
                type: ResourceItemTypes.MATERIAL,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === ResourceItemTypes.MATERIAL),
                takeUntil(this.destroy)
            )
            .subscribe((data) => this.initImgTB(data, index));
    }
    private initImgTB(data: any, index?: number) {
        if (data.value === ResourceMediaTypes.FILE) {
            if (data.file) {
                data.file.medias?.file?.forEach((mediaId: number) => {
                    const group = this.createItemFormGroup(
                        new ResourceItem({
                            file_media_id: mediaId,
                            name: mediaId.toString(),
                        })
                    );
                    group.markAsDirty();
                    if (index) {
                        this.items.insert(index, group);
                    } else {
                        this.items.push(group);
                    }
                });

                this.items.markAsDirty();
                this.openedIndex = index;
                this.host.performResSubmit({ eager: 'items' });
            } else {
                this.duplicateChildItem(
                    {
                        title: data.media.filename,
                        name: data.media.path,
                        item_type_value: data.media.type,
                    } as ResourceItem,
                    index
                );
            }
        } else {
            this.addAIImg(data.aiText, index);
        }
        this.toggleAutoSave(true);
    }

    addAIImg(prompt: string, index: number) {
        // https://git.klickportalen.se/klickdata2016/nk3-datastore/issues/336 >> BE issue
        this.messageService.openMessage(
            MessageErrorComponent,
            $localize`AI Image Generation is not supported in the groups container yet.`
        );
        return;
        this.addNewItem({ title: prompt, item_type_value: 'image/gpt' } as ResourceItem, index);
        this.prepareResource([index])
            .pipe(
                takeUntil(this.destroy),
                switchMap((ids) => {
                    this.updateItemsPromptStatus(ids);
                    return this.itemService.regenerateItems(ids, {
                        body: {
                            type: 'image',
                            model_name: this.host.agent.value,
                        },
                    });
                })
            )
            .subscribe();
    }
    toggleAutoSave(enabled: boolean) {
        this.host.toggleAutoSave(enabled);
    }
    splitText(blocksCount: number, index: number) {
        //     this.onLoading.emit(true);
        //     const itemToSplitCtrl = this.items.at(index);
        //     const itemData: ResourceItemData = {
        //         title: itemToSplitCtrl.value.title || Utils.getHeadline(itemToSplitCtrl.value.name),
        //         item_type_value: itemToSplitCtrl.value.item_type_value,
        //         language_id: itemToSplitCtrl.value.language_id,
        //     };
        //     const generatedIndices = Array.from({ length: blocksCount }, (_, i) => index + i + 1);
        //     generatedIndices.forEach((generatedIndex) =>
        //         this.duplicateChildItem(new ResourceItem(itemData), generatedIndex)
        //     );
        //     this.prepareResource(generatedIndices)
        //         .pipe(
        //             switchMap((ids: number[]) => {
        //                 this.updateItemsPromptStatus(ids);
        //                 const parentId = this.items.at(index).value.id;
        //                 return this.itemService.splitBlocks([parentId], ids);
        //             }),
        //             takeUntil(this.destroy)
        //         )
        //         .subscribe(() => {
        //             this.onLoading.emit(false);
        //         });
    }
    private prepareResource(indices: number[]): Observable<number[]> {
        const resourceItemsIsDirty = this.items.controls.some(
            (ctl) => ctl.dirty && (ctl.value.id || ctl.value.name || ctl.value.title)
        );
        const shouldPrepareSubmit = !this.resource?.id || resourceItemsIsDirty;
        const obs = shouldPrepareSubmit ? this.host.prepareSubmit({ eager: 'items' }) : of(this.resource);

        return <Observable<number[]>>obs.pipe(
            tap((resource) => this.host.updateMainResource(resource)),
            switchMap(() => this.initContainerChildren()),
            map(() => this.idsFromIndices(indices))
        );
    }
    private idsFromIndices(indices: number[]): number[] {
        return this.items.controls
            .filter((item, index) => indices.includes(index))
            .map((ctl, i) => ctl.value.id || indices[i]);
    }
    private updateItemsPromptStatus(ids: number[]) {
        setTimeout(() => ids.forEach((id) => this.items.controls.find((group) => group.value.id === id)?.disable()));
    }
    delete(id: number, index: number) {
        if (this.isEditInBottomSheet) {
            this.editInBottomSheet();
            return;
        }
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'delete',
                title: $localize`Delete item`,
                contentBody: $localize`You are about to delete this item. Please use caution and make sure that you really intend to delete before confirming delete below.`,
                positiveBtn: $localize`Delete`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                filter((res) => !!res),
                switchMap(() => {
                    if (id) {
                        return this.itemService.destroy([id]);
                    } else {
                        return of(index);
                    }
                }),
                takeUntil(this.destroy)
            )
            .subscribe(() => {
                this.items.removeAt(index);
                this.itemForm.markAsDirty();
            });
    }
    editInBottomSheet() {
        this.messageService.openMessage(
            MessageSavedComponent,
            $localize`This action can be done when edit in the source, please click on 'Edit Material source' button`
        );
    }
    onSortPopup(index: number, sortingPrio: number) {
        moveItemInArray(this.items.controls, index, sortingPrio);
        this.items.markAsDirty();
        this.itemForm.markAsDirty();
    }
    onAddNewBlockInContainer(data: { index: number; blockType: string }) {
        switch (data.blockType) {
            case 'text':
                this.duplicateChildItem(new ResourceItem({ item_type_value: 'text_material' }), data.index + 1);
                break;
            case 'image':
                this.addImg(data.index + 1);
                break;
            // case 'file':
            //     this.duplicateChildItem(new ResourceItem({ item_type_value: 'file_material' }), data.index + 1);
            //     break;
            case 'link':
                this.duplicateChildItem(new ResourceItem({ item_type_value: 'url_material' }), data.index + 1);
                break;
        }
    }
    /**
     * Handle drag started event and delegate to host
     */
    onDragStarted(event?: any): void {
        // Delegate to host component for complete drag management
        if (this.host && this.host.onDragStarted) {
            console.log('🏠 Delegating drag start to host from container');
            this.host.onDragStarted(event);
        }

        this.cdr.markForCheck();
    }

    /**
     * Handle drag moved event and delegate to host
     */
    onDragMoved(event: any): void {
        // Delegate to host component for hover expansion detection
        if (this.host && this.host.onDragMoved) {
            console.log('🎯 Delegating drag move to host from container');
            this.host.onDragMoved(event);
        }
    }

    /**
     * Handle drag ended event and delegate to host
     */
    onDragEnded(): void {
        // Delegate to host component for complete drag management
        if (this.host && this.host.onDragEnded) {
            console.log('🏁 Delegating drag end to host from container');
            this.host.onDragEnded();
        }

        // Add a small delay to ensure FormArray is updated after cross-container moves
        setTimeout(() => {
            // Force change detection to update counts and UI
            this.cdr.markForCheck();
            this.cdr.detectChanges();

            // Log detailed count information for debugging
            console.log('📊 Children count after drag end:', {
                actualCount: this.childrenItemsCount,
                formArrayLength: this.items?.controls?.length,
                filteredLength: this.items?.controls?.filter((item) => !item.value.deleted)?.length,
                childrenIdsLength: this.childrenIds?.length,
                isEmpty: !this.items?.controls?.length,
            });
        }, 50);
    }

    drop(event: CdkDragDrop<FormArray>) {
        console.log('📦 Container drop event:', event);

        // Handle reordering within the same container
        if (event.previousContainer === event.container) {
            console.log('🔄 Reordering within container');
            // Use Angular CDK's moveItemInArray for same-container reordering
            moveItemInArray(this.items.controls, event.previousIndex, event.currentIndex);
            this.items.markAsDirty();
            this.items.updateValueAndValidity();
            console.log('✅ Container reordering completed');
        } else {
            console.log('↔️ Cross-container drop - delegating to main component');
            // For cross-container drops, delegate to the main component
            this.host.handleDrop(event);
        }

        // Add a small delay to ensure FormArray changes are processed
        setTimeout(() => {
            // Force change detection and update counts
            this.cdr.markForCheck();
            this.cdr.detectChanges();

            // Log detailed count information for debugging
            console.log('📊 Updated children count after drop:', {
                actualCount: this.childrenItemsCount,
                formArrayLength: this.items?.controls?.length,
                filteredLength: this.items?.controls?.filter((item) => !item.value.deleted)?.length,
                childrenIdsLength: this.childrenIds?.length,
                isEmpty: !this.items?.controls?.length,
            });
        }, 50);
    }

    onDropListEntered(event: any): void {
        console.log('🎯 Empty drop zone entered:', event);
    }

    onDropListExited(event: any): void {
        console.log('🚪 Empty drop zone exited:', event);
    }

    handleDisplayMode() {
        if (!this.resourceItems.length) return;
        if (this.isEditInBottomSheet) {
            this.editInBottomSheet();
            return;
        }
        this.bottomSheet
            .open(MaterialParentDisplayModeBottomSheetComponent, {
                data: {
                    id: this.itemForm.value.id,
                    resourceItems: this.resourceItems,
                },
            })
            .afterDismissed()
            .pipe(
                takeUntil(this.destroy),
                filter((result) => !!result)
            )
            .subscribe((value) => {
                this.items.controls.forEach((item) => {
                    const leftSideIndex = value.leftSideItems.findIndex((v) => v.id === item.value.id);
                    const rightSideIndex = value.rightSideItems.findIndex((v) => v.id === item.value.id);
                    const align =
                        leftSideIndex !== -1
                            ? 'left'
                            : rightSideIndex !== -1
                            ? 'right'
                            : this.getDefaultLayoutAlign(item.value.item_type_value);
                    const order = leftSideIndex !== -1 ? leftSideIndex : rightSideIndex !== -1 ? rightSideIndex : null;
                    const matchedResItem = this.resourceItems.find(
                        (resItem) => resItem.id && resItem.id === item.value.id
                    );
                    if (!!matchedResItem) {
                        if (!matchedResItem.metadata) {
                            matchedResItem.metadata = {};
                        }
                        matchedResItem.metadata.layout = {
                            align: align,
                            order: order,
                        };
                    }

                    item.patchValue({
                        align: align,
                        order: order,
                    });
                });
                this.items.markAsDirty();
                this.itemForm.markAsDirty();
            });
    }
    private createItemFormGroupFromResourceItem(item: ResourceItem): FormGroup {
        const formGroupConfig = {};
        if (item.id !== undefined) formGroupConfig['id'] = item.id;
        if (item.name !== undefined) formGroupConfig['name'] = item.name;
        if (item.title !== undefined) formGroupConfig['title'] = item.title;
        if (item.updated_at !== undefined) formGroupConfig['updated_at'] = item.updated_at;
        if (item.text_validation !== undefined) formGroupConfig['text_validation'] = item.text_validation;
        if (item.language_id !== undefined) formGroupConfig['language_id'] = item.language_id;
        if (item.parent_id !== undefined) formGroupConfig['parent_id'] = item.parent_id;
        if (item.total_tokens !== undefined) formGroupConfig['total_tokens'] = item.total_tokens;
        if (item.status !== undefined) formGroupConfig['status'] = item.status;
        if (item.prompt_status !== undefined) formGroupConfig['prompt_status'] = item.prompt_status;
        if (item.item_type_value !== undefined) formGroupConfig['item_type_value'] = item.item_type_value;
        if (item.deleted !== undefined) formGroupConfig['deleted'] = item.deleted;
        if (item.content !== undefined) formGroupConfig['content'] = item.content;
        if (item.model_name !== undefined) formGroupConfig['model_name'] = item.model_name;
        if (item.file_item_id !== undefined) formGroupConfig['file_item_id'] = item.file_item_id;

        formGroupConfig['align'] = item.metadata?.layout?.align ?? this.getDefaultLayoutAlign(item.item_type_value);
        formGroupConfig['order'] = item.metadata?.layout?.order;
        formGroupConfig['item_visibility'] = item.metadata?.visibility?.item;
        formGroupConfig['headline_visibility'] =
            item.metadata?.visibility?.headline ?? this.host.getDefaultHeadlineVisibility(item.item_type_value);

        return this.fb.group(formGroupConfig);
    }
    private createItemFormGroup(item: ResourceItem): FormGroup {
        return this.fb.group({
            id: item.id,
            name: item.name,
            title: item.title,
            updated_at: item.updated_at,
            text_validation: item.text_validation || 'empty',
            language_id: item.language_id,
            parent_id: item.parent_id,
            total_tokens: item.total_tokens,
            status: item.status,
            prompt_status: item.prompt_status,
            item_type_value: item.item_type_value,
            deleted: item.deleted,
            content: item.content,
            model_name: item.model_name,
            file_item_id: item.file_item_id,
            align: item.metadata?.layout?.align ?? this.getDefaultLayoutAlign(item.item_type_value),
            order: item.metadata?.layout?.order,
            item_visibility: item.metadata?.visibility?.item ?? true,
            headline_visibility:
                item.metadata?.visibility?.headline ?? this.host.getDefaultHeadlineVisibility(item.item_type_value),
        });
    }
    private getDefaultLayoutAlign(itemType: string): string {
        if (itemType.indexOf('image') !== -1) return 'right';
        if (itemType.indexOf('video') !== -1) return 'right';
        if (itemType.indexOf('audio') !== -1) return 'right';
        return 'left';
    }

    duplicateChildItem(item: ResourceItem, index: number) {
        const newItemCtl = this.createItemFormGroup(item);
        this.items.insert(index, newItemCtl);
        newItemCtl.patchValue({ id: null });
        newItemCtl.markAsDirty();
        this.openedIndex = index;
        this.cdr.markForCheck();
    }
    addNewItem(item: ResourceItem, index: number) {
        const newItemCtl = this.createItemFormGroupFromResourceItem(item);
        this.items.insert(index, newItemCtl);
        newItemCtl.patchValue({ id: null });
        newItemCtl.markAsDirty();
        this.openedIndex = index;
    }
    replaceItemFormGroupById(item: ResourceItem) {
        const newItemCtl = this.createItemFormGroup(item);
        if (
            item.prompt_status === 'PENDING' ||
            item.prompt_status === 'PROCESSING' ||
            item.prompt_status === 'PARSING'
        ) {
            newItemCtl.disable();
        }
        if (item.prompt_status == 'ERROR') {
            this.messageService.openMessage(MessageErrorComponent);
        }
        const existingItemIndex = this.items.controls.findIndex((targetItem) => targetItem.value.id === item.id);
        if (existingItemIndex !== -1) {
            this.items.controls[existingItemIndex] = newItemCtl;
        } else {
            this.items.controls.push(newItemCtl);
        }
    }
    download() {
        const ids = this.expansionPanel.expanded
            ? this.items.controls.filter((item) => !item.value.deleted && !!item.value.id).map((item) => item.value.id)
            : this.childrenIds;

        this.onDownload.emit(ids);
    }
}
